import type { ApiErrorBody } from '@familienbaum/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Erste Meldung zu einem Formularfeld, falls vorhanden. */
  fieldError(name: string): string | undefined {
    return this.fields?.[name]?.[0];
  }
}

/** Wird ausgelöst, wenn die Sitzung abgelaufen ist. */
export const UNAUTHORIZED_EVENT = 'fb:unauthorized';

async function toApiError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* keine JSON-Antwort */
  }
  return new ApiError(
    res.status,
    body?.error.code ?? 'http_error',
    body?.error.message ?? `Anfrage fehlgeschlagen (${res.status})`,
    body?.error.fields,
  );
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; formData?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    signal: options.signal,
  };

  if (options.formData) {
    init.body = options.formData;
  } else if (options.body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }

  const res = await fetch(`/api${path}`, init);

  if (res.status === 401) {
    // Der Router hört darauf und schickt zur Anmeldung.
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw await toApiError(res);
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;

  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, formData: FormData) => request<T>('POST', path, { formData }),
};
